import FormQuestionType from '@opsbase/lib-form/FormQuestionType/FormQuestionType';
import i18n from '@opsbase/lib-i18n';
import opIs from '@opsbase/lib-op/helpers/opIs';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useOpDetailsReportURL from '@opsbase/react-gql-hooks/op/useOpDetailsReportURL';
import IssueStatusCountChips from '@opsbase/web-chips/IssueStatusCountChips';
import ReportDownloadIcon from '@opsbase/web-icons/icons/ReportDownloadIcon';
import ReportViewIcon from '@opsbase/web-icons/icons/ReportViewIcon';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav';
import Tab from '@opsbase/web-tabs/Tab';
import Tabs from '@opsbase/web-tabs/Tabs';
import Button from '@opsbase/web-ui/Button';
import { Tooltip } from '@opsbase/web-ui/Tooltip';
import React, { useCallback, useMemo, useState } from 'react';
import OpViewAnswers from './OpViewAnswers';
import OpViewConversations from './OpViewConversations';
import OpViewIssues from './OpViewIssues';
import OpViewOverview from './OpViewOverview';
export function OpView(props) {
    var _a;
    const { op } = props;
    const [selectedTab, setSelectedTab] = useState(0);
    const { pathname, goTo } = useNav();
    const self = useSelf();
    const { data: opDetailsReportUrl } = useOpDetailsReportURL({
        variables: { id: op.id },
    });
    const canApprove = useMemo(() => canApproveOp(op, self), [op, self]);
    const handleChangeSelectedTab = useCallback((e, v) => {
        setSelectedTab(v);
    }, []);
    const handleClickViewReport = useCallback(() => {
        window.open(opDetailsReportUrl, '_blank');
    }, [opDetailsReportUrl]);
    const handleClickApprove = useCallback(() => {
        goTo(Pages.OP_APPROVE, { id: op.id });
    }, [goTo, op]);
    const conversationCount = useMemo(() => {
        const form = op.getParsedForm();
        return Object.values(form.answers)
            .map(z => z.conversationId)
            .filter(z => z).length;
    }, [op]);
    const hasIssues = (_a = op.issues) === null || _a === void 0 ? void 0 : _a.length;
    const hasNotes = conversationCount > 0;
    const sectionQuestions = useMemo(() => { var _a, _b; return (_b = (_a = op.form) === null || _a === void 0 ? void 0 : _a.filter(q => q.type === FormQuestionType.SECTION)) !== null && _b !== void 0 ? _b : []; }, [op.form]);
    const refs = useMemo(() => {
        return sectionQuestions.reduce((acc, value) => {
            acc[value.qId] = React.createRef();
            return acc;
        }, {});
    }, [sectionQuestions]);
    const handleClick = useCallback(id => {
        var _a, _b;
        (_b = (_a = refs[id]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, [refs]);
    return (React.createElement(Horizontal, { stretch: true },
        React.createElement(Pane, { style: { width: 340 } },
            React.createElement(OpViewOverview, { op: op, onClick: handleClick })),
        React.createElement(Vertical, { style: { overflow: 'hidden' } },
            (hasIssues || hasNotes) && (React.createElement(Pane, { style: { paddingLeft: 16 } },
                React.createElement(Tabs, { value: selectedTab, onChange: handleChangeSelectedTab },
                    React.createElement(Tab, { label: i18n.t('answers'), value: 0 }),
                    !!hasIssues && (React.createElement(Tab, { label: React.createElement(Horizontal, { gutter: true },
                            React.createElement(Pane, null, i18n.t('issues')),
                            React.createElement(IssueStatusCountChips, { issues: op.issues })), value: 1 })),
                    !!hasNotes && (React.createElement(Tab, { label: React.createElement(Horizontal, { gutter: true },
                            React.createElement(Pane, null, i18n.t('notes')),
                            React.createElement(Horizontal, { centered: true, justify: "flex-start" },
                                React.createElement(Pane, { style: {
                                        width: 18,
                                        height: 18,
                                        marginRight: 2,
                                        lineHeight: '18px',
                                        fontSize: 10,
                                        textAlign: 'center',
                                        borderRadius: '50%',
                                        backgroundColor: Palette.BackgroundGrey.main,
                                    } },
                                    React.createElement(Tooltip, { tip: conversationCount }, `${conversationCount}`)))), value: 2 }))))),
            React.createElement(Pane, { grow: true },
                selectedTab === 0 && React.createElement(OpViewAnswers, { op: op, refs: refs }),
                !!hasIssues && selectedTab === 1 && React.createElement(OpViewIssues, { op: op }),
                !!hasNotes && selectedTab === 2 && React.createElement(OpViewConversations, { op: op })),
            !!canApprove && (React.createElement(Pane, { center: true, padding: true },
                React.createElement(Button, { style: { minWidth: 200, height: 42, fontSize: 24, lineHeight: '28px' }, label: i18n.t('approve'), color: Palette.positive, onClick: handleClickApprove }))),
            !canApprove && !!op.actualStart && (React.createElement(Horizontal, { shrink: true, gutter: true, justify: 'flex-end', padding: true },
                React.createElement(Button, { icon: React.createElement(ReportViewIcon, { size: 16 }), label: i18n.t('view-details-report'), color: Palette.positive, onClick: handleClickViewReport }),
                React.createElement(Button, { icon: React.createElement(ReportDownloadIcon, { size: 16 }), label: i18n.t('download-details-report'), link: Pages.OP_GENERATE_DETAILS_REPORT.replace(':id', op.id).replace('*', pathname) }))))));
}
export default OpView;
function canApproveOp(op, self) {
    if (!opIs.approvable(op))
        return false;
    if (!!op.staff.find(s => s.id === self.id))
        return true;
    if (!!op.positions.find(p => self.accessiblePositions.includesId(p.id)))
        return true;
    return !!op.teams.find(p => self.accessibleTeams.includesId(p.id));
}
